"use client";

import { useEffect, useRef } from "react";
import { reportClientError } from "@/lib/client-error-beacon";

/**
 * Boundary de último recurso do App Router: captura erros lançados pelo
 * PRÓPRIO root layout (que o error.tsx de segmento não alcança) e substitui
 * a tela nativa do Next ("Application error: a server-side exception...").
 *
 * Regras deste arquivo: renderiza <html>/<body> próprios, sem providers nem
 * hooks de contexto (o layout pode ter falhado antes de montá-los), estilos
 * inline (globals.css pode não ter carregado) e translate="no" (o texto não
 * pode ser mutado pelo Google Translate — ver TranslationDomGuard).
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const reported = useRef(false);
  useEffect(() => {
    console.error("Root Layout Error:", error);
    if (!reported.current) {
      reported.current = true;
      reportClientError(error);
    }
  }, [error]);

  return (
    <html lang="pt-br">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
          fontFamily:
            "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          backgroundColor: "#0b0d12",
          color: "#e5e7eb",
        }}
        translate="no"
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(239,68,68,0.12)",
            color: "#ef4444",
            fontSize: "28px",
            fontWeight: 800,
          }}
          aria-hidden
        >
          !
        </div>

        <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 800, letterSpacing: "-0.02em" }}>
          Algo deu errado
        </h1>

        <p style={{ margin: 0, maxWidth: "420px", fontSize: "14px", color: "#9ca3af", lineHeight: 1.6 }}>
          Ocorreu um erro temporário ao carregar a página. Recarregue para tentar novamente.
        </p>

        <div style={{ display: "flex", gap: "12px", marginTop: "8px", flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={() => reset()}
            style={{
              minHeight: "44px",
              padding: "0 24px",
              borderRadius: "8px",
              border: "1px solid #374151",
              backgroundColor: "transparent",
              color: "#e5e7eb",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Tentar novamente
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              minHeight: "44px",
              padding: "0 24px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#48c1ce",
              color: "#08262a",
              fontSize: "14px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Recarregar a página
          </button>
        </div>

        {error?.digest && (
          <p
            style={{
              marginTop: "16px",
              fontSize: "10px",
              fontFamily: "monospace",
              color: "#4b5563",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
            }}
          >
            ID do Erro: {error.digest}
          </p>
        )}
      </body>
    </html>
  );
}
